import React from 'react';
import SchoolIcon from "@mui/icons-material/School";
import { requestTutorial } from "Tutorial/tutorialStore";
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import SplineIcon from '@mui/icons-material/ShowChart';
import AboutIcon from '@mui/icons-material/Info';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsIcon from '@mui/icons-material/Settings';
import TutorialIcon from '@mui/icons-material/PlayCircle';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import { useAuth } from '../../context/AuthContext';
import SettingsPanel from '../Modals/SettingsModal/SettingsPanel';
import './Footer.css'
import SupportModal from 'components/Modals/SupportModal/SupportModal';
import { peekPendingTicketFields } from 'Voice/voiceCommands';
import { SaveSettings, scheduleAutosave } from 'components/Viewer3d/SaveManager';
import { useViewerStore } from 'context/ViewerContext';
import { MeasurementSystem } from 'components/Viewer3d/consts';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { canUseDevelopmentUi } from 'auth/permissions';

const Footer: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { logout, user, isExhibition } = useAuth();
  const canUseChatbox = canUseDevelopmentUi(user, isExhibition);
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = React.useState(false);
  const [ticketTitle, setTicketTitle] = React.useState('');
  const [ticketDescription, setTicketDescription] = React.useState('');
  const gcodeStatus = useViewerStore((state) => state.gcodeStatus);
  const markerPosition = useViewerStore((state) => state.markerPosition);
  const measurementSystem = useViewerStore((state) => state.measurementSystem);
  const gcodeStatusMessages = new Map<string, string>([
    ["idle", ""],
    ["engrave", "Generating G-code..."],
    ["fetch", "Transfering data..."],
  ])

  const handleLogoutClick = () => {
    scheduleAutosave('Current design', {
      now: true
    } as Partial<SaveSettings>).then(() => {
      logout();
    });
  };

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
  };

  React.useEffect(() => {
    const applyTicketFields = (detail?: { title?: string; description?: string }) => {
      const pending = peekPendingTicketFields();
      const title = (detail?.title || pending.title || "").trim();
      const description = (detail?.description || pending.description || "").trim();
      if (title) setTicketTitle(title);
      if (description) setTicketDescription(description);
    };
    const onOpenSettings = () => setIsSettingsOpen(true);
    const onOpenTickets = (event: Event) => {
      applyTicketFields((event as CustomEvent).detail);
      setIsSupportModalOpen(true);
    };
    const onTicketUpdated = (event: Event) => {
      applyTicketFields((event as CustomEvent).detail);
      setIsSupportModalOpen(true);
    };
    const onClosePanels = (event: Event) => {
      const keep = (event as CustomEvent).detail?.keep;
      if (keep !== "settings") setIsSettingsOpen(false);
      if (keep !== "tickets") setIsSupportModalOpen(false);
    };
    document.addEventListener("voice:openSettings", onOpenSettings);
    document.addEventListener("voice:openTickets", onOpenTickets as EventListener);
    document.addEventListener("voice:ticketUpdated", onTicketUpdated as EventListener);
    document.addEventListener("voice:closePanels", onClosePanels as EventListener);
    return () => {
      document.removeEventListener("voice:openSettings", onOpenSettings);
      document.removeEventListener("voice:openTickets", onOpenTickets as EventListener);
      document.removeEventListener("voice:ticketUpdated", onTicketUpdated as EventListener);
      document.removeEventListener("voice:closePanels", onClosePanels as EventListener);
    };
  }, []);

  const toggleMetric = () => {
    useViewerStore.getState().setMeasurementSystem(measurementSystem == MeasurementSystem.imperial ? MeasurementSystem.metric : MeasurementSystem.imperial);
  }

  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-left">
          {gcodeStatus.result === "idle" ? (
            markerPosition ? (
              <span className="footer-text">
                Position: X:{markerPosition.x} Y:{markerPosition.y} Z:{markerPosition.z}
              </span>
            ) : (
              <span className="footer-text">Hover over model to see position</span>
            )
          ) : (
            <span className="footer-text">{gcodeStatusMessages.get(gcodeStatus.result)}</span>
          )}
        </div>
        <div className="footer-center">
          {/* <Link to="/spline-editor" className="footer-button">
            <SplineIcon className="footer-icon" />
            <span>Spline Editor</span>
          </Link>
          <button className="footer-button">
            <AboutIcon className="footer-icon" />
            <span>About</span>
          </button> */}
          <button className="footer-button" data-tour-id="measurement" onClick={toggleMetric} title="Switch Metric">
            <ChangeCircleIcon className="footer-icon" />
            <span className="footer-text">{measurementSystem== MeasurementSystem.imperial ? "Imperial" : "Metric"}</span>
          </button>
          {canUseChatbox && (
            <button
              className="footer-button"
              data-tour-id="tutorial"
              onClick={requestTutorial}
              title="Have the assistant walk you through the interface"
            >
              <SchoolIcon className="footer-icon" />
              <span className="footer-text">Tutorial</span>
            </button>
          )}
          <button className="footer-button" data-tour-id="theme" onClick={toggleTheme} title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
            {isDarkMode ? (
              <LightModeIcon className="footer-icon" />
            ) : (
              <DarkModeIcon className="footer-icon" />
            )}
            <span className="footer-text">Theme</span>
          </button>
          <button className="footer-button" data-tour-id="settings" onClick={handleSettingsClick}>
            <SettingsIcon className="footer-icon" />
            <span className="footer-text">Settings</span>
          </button>
          <button className="footer-button" data-tour-id="tickets" onClick={() => setIsSupportModalOpen(true)} title={"Send Tickets"}>
            <HelpRoundedIcon className="footer-icon" />
            <span className="footer-text">Tickets</span>
          </button>
          <SupportModal
            isOpen={isSupportModalOpen}
            title={ticketTitle}
            description={ticketDescription}
            onTitleChange={setTicketTitle}
            onDescriptionChange={setTicketDescription}
            onClose={() => setIsSupportModalOpen(false)}
          />
        </div>
        <div className="footer-right">
          <span className="footer-text">© 2026 T-Soles</span>
          <span className="footer-text">Version 4.5.0</span>
          <button onClick={handleLogoutClick} className="footer-logout-button" title="Logout">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span className="footer-text">Logout</span>
          </button>
        </div>
      </div>
      <SettingsPanel isOpen={isSettingsOpen} onClose={handleSettingsClose} />
    </footer>
  );
};

export default Footer;
